import { Serializable } from "@redotech/json/serialize";
import * as z from "zod";

export const unaryRpc = <
  Name extends string,
  Input extends Serializable,
  Output extends Serializable,
>(schemas: {
  name: Name;
  description?: string;
  mutates?: boolean;
  skipOutputValidation?: boolean;
  input: z.ZodType<Input>;
  output: z.ZodType<Output>;
  mock?: Output | ((generated: Output, input: Input) => Output);
}): UnaryRpcSchema<Name, Input, Output> => ({
  ...schemas,
  method: "unary" as const,
});

export const unaryStreamRpc = <
  Name extends string,
  Input extends Serializable,
  Output extends Serializable,
>(schemas: {
  name: Name;
  input: z.ZodType<Input>;
  output: z.ZodType<Output>;
  mock?: Output | ((generated: Output, input: Input) => Output);
}): UnaryStreamRpcSchema<Name, Input, Output> => ({
  ...schemas,
  method: "unary-stream" as const,
});

export interface UnaryRpcSchema<Name extends string, Input, Output> {
  name: Name;
  /** Human/agent-readable explanation of what the endpoint does and how to use it. */
  description?: string;
  /** Marks a state-changing endpoint, so a code-mode agent stages it for confirmation instead of running it. */
  mutates?: boolean;
  /** Skips the server-side `output` parse. Only safe when the handler returns plain data (no
   * Mongoose docs — `dehydrate` throws on their cycles) with exactly the schema's keys. */
  skipOutputValidation?: boolean;
  input: z.ZodType<Input>;
  output: z.ZodType<Output>;
  method?: "unary" | undefined;
  /** Optional mock override. When mock mode is enabled:
   *  - If a value, it is returned directly.
   *  - If a function, it receives the auto-generated value and the call's
   *    input (so cursor-paged RPCs can mock per page) and returns the final mock. */
  mock?: Output | ((generated: Output, input: Input) => Output);
}

export interface UnaryStreamRpcSchema<Name extends string, Input, Output> {
  name: Name;
  input: z.ZodType<Input>;
  output: z.ZodType<Output>;
  method: "unary-stream";
  /** Optional mock override. When mock mode is enabled:
   *  - If a value, it is yielded directly.
   *  - If a function, it receives the auto-generated value and the call's
   *    input and returns the final mock. */
  mock?: Output | ((generated: Output, input: Input) => Output);
}

export type RpcSchema<Name extends string, Input, Output> =
  | UnaryRpcSchema<Name, Input, Output>
  | UnaryStreamRpcSchema<Name, Input, Output>;

export interface RpcClientSchema<Name extends string, Input, Output> {
  name: Name;
  input: Input;
  output: Output;
  method: "unary-stream" | "unary";
}

export interface RpcDefinition {
  [key: string]: RpcSchema<string, any, any>;
}

export interface RpcClientDefinition {
  [key: string]: RpcClientSchema<string, any, any>;
}

export type InferRpcDefinition<T> = T extends RpcDefinition
  ? { [key in keyof T]: InferRpcSchema<T[key]> }
  : never;

type SerializableOrError<T> = T extends Serializable
  ? T
  : "Error: unserializable data";

// z.output reads the schema's output type directly; inferring it through
// z.ZodType<I> costs seconds of check time on large definitions.
type InferRpcSchema<T> = T extends {
  name: infer N;
  input: infer I;
  output: infer O;
  method: "unary-stream";
}
  ? {
      name: N;
      input: SerializableOrError<z.output<I>>;
      output: SerializableOrError<z.output<O>>;
      method: "unary-stream";
    }
  : T extends { name: infer N; input: infer I; output: infer O }
    ? {
        name: N;
        input: SerializableOrError<z.output<I>>;
        output: SerializableOrError<z.output<O>>;
        method: "unary";
      }
    : never;
